/**
 * Voxel hexahedral FEM — linear elasticity with von Mises stress recovery.
 *
 * Pipeline: watertight surface tessellation → voxel grid (X-column ray
 * parity) → 8-node trilinear hex elements. Every voxel is an identical cube,
 * so ONE 24×24 element stiffness matrix (2×2×2 Gauss) serves the whole
 * model, and K·p products run matrix-free element-by-element — no global
 * matrix, tiny memory, robust on any watertight body including imported
 * STEP. Solved with Jacobi-preconditioned conjugate gradients.
 *
 * Boundary conditions bind to B-rep FACES: a fixture/load names a point, the
 * nearest display triangle's face id selects the whole face, and grid nodes
 * within ~¾ voxel of its triangles receive the constraint/load.
 *
 * Units: mm, N, MPa (E in MPa) → displacements mm, stress MPa.
 *
 * Accuracy notes: stair-stepped boundaries and full-integration trilinear
 * hexes (mildly stiff in bending) make this a design-guidance tool, not a
 * certification tool — the same trade every voxel FEM makes. Validated in
 * tests against uniaxial σ=F/A, δ=FL/EA and an Euler-Bernoulli cantilever.
 */

export interface FeaBC {
  point: [number, number, number];
}

export interface FeaLoad extends FeaBC {
  /** Total force vector (N), distributed over the face's surface nodes. */
  force: [number, number, number];
}

export interface FeaInput {
  positions: Float32Array;
  indices: Uint32Array;
  /** B-rep face id per triangle (face-based BC selection). */
  triFaceIndex?: Uint32Array;
  /** Young's modulus, MPa. */
  E: number;
  /** Poisson's ratio. */
  nu: number;
  /** Yield strength, MPa (factor of safety = yield / max von Mises). */
  yieldStrength: number;
  fixtures: FeaBC[];
  loads: FeaLoad[];
  /** Voxels along the longest bbox axis (default 36, clamped 8..96). */
  resolution?: number;
  /** Active-element budget — auto-coarsens above it (default 40k). */
  maxElements?: number;
  /** PCG relative residual target (default 1e-4). */
  tolerance?: number;
  maxIterations?: number;
  onProgress?: (phase: 'voxelize' | 'solve' | 'stress', pct: number) => void;
}

export interface FeaResult {
  converged: boolean;
  iterations: number;
  elements: number;
  nodes: number;
  /** Voxel edge length used (mm). */
  voxelSize: number;
  maxVonMises: number;
  hotspot: [number, number, number];
  maxDisplacement: number;
  maxDisplacementAt: [number, number, number];
  factorOfSafety: number;
  /** Von Mises (MPa) sampled at every input mesh vertex — display colors. */
  vertexVonMises: Float32Array;
  /** Displacement (mm, unscaled xyz) per input mesh vertex — deformed view. */
  vertexDisplacement: Float32Array;
  /** Display vertices with no nearby element (features below voxel size). */
  unmappedVertices: number;
}

/** Local hex node offsets (i,j,k) and matching ξηζ signs — standard order. */
const NODE_OFF: readonly [number, number, number][] = [
  [0, 0, 0], [1, 0, 0], [1, 1, 0], [0, 1, 0],
  [0, 0, 1], [1, 0, 1], [1, 1, 1], [0, 1, 1],
];
const SIGN: readonly [number, number, number][] = NODE_OFF.map(
  ([a, b, c]) => [a * 2 - 1, b * 2 - 1, c * 2 - 1] as [number, number, number],
);

/** Isotropic elasticity matrix D (6×6, engineering shear strains), MPa. */
export function elasticityMatrix(E: number, nu: number): Float64Array {
  const lam = (E * nu) / ((1 + nu) * (1 - 2 * nu));
  const mu = E / (2 * (1 + nu));
  const D = new Float64Array(36);
  for (let i = 0; i < 3; i++) {
    for (let j = 0; j < 3; j++) D[i * 6 + j] = i === j ? lam + 2 * mu : lam;
    D[(i + 3) * 6 + (i + 3)] = mu;
  }
  return D;
}

/** Strain-displacement matrix B (6×24) at natural coords (ξ,η,ζ). */
function bMatrix(xi: number, eta: number, zeta: number, h: number): Float64Array {
  const B = new Float64Array(6 * 24);
  const s = 2 / h; // d(natural)/d(physical) for a cube of edge h
  for (let n = 0; n < 8; n++) {
    const [sx, sy, sz] = SIGN[n]!;
    const dNdx = (sx / 8) * (1 + eta * sy) * (1 + zeta * sz) * s;
    const dNdy = (sy / 8) * (1 + xi * sx) * (1 + zeta * sz) * s;
    const dNdz = (sz / 8) * (1 + xi * sx) * (1 + eta * sy) * s;
    const c = n * 3;
    B[0 * 24 + c] = dNdx;
    B[1 * 24 + c + 1] = dNdy;
    B[2 * 24 + c + 2] = dNdz;
    B[3 * 24 + c] = dNdy; B[3 * 24 + c + 1] = dNdx;
    B[4 * 24 + c + 1] = dNdz; B[4 * 24 + c + 2] = dNdy;
    B[5 * 24 + c] = dNdz; B[5 * 24 + c + 2] = dNdx;
  }
  return B;
}

/** Element stiffness Ke (24×24) for a cube voxel of edge h — 2×2×2 Gauss. */
export function hexStiffness(E: number, nu: number, h: number): Float64Array {
  const D = elasticityMatrix(E, nu);
  const Ke = new Float64Array(24 * 24);
  const g = 1 / Math.sqrt(3);
  const detW = (h / 2) ** 3; // |J| × weight(=1) per Gauss point
  for (const gx of [-g, g]) for (const gy of [-g, g]) for (const gz of [-g, g]) {
    const B = bMatrix(gx, gy, gz, h);
    // Ke += Bᵀ D B detW
    const DB = new Float64Array(6 * 24);
    for (let i = 0; i < 6; i++) {
      for (let j = 0; j < 24; j++) {
        let sum = 0;
        for (let k = 0; k < 6; k++) sum += D[i * 6 + k]! * B[k * 24 + j]!;
        DB[i * 24 + j] = sum;
      }
    }
    for (let i = 0; i < 24; i++) {
      for (let j = 0; j < 24; j++) {
        let sum = 0;
        for (let k = 0; k < 6; k++) sum += B[k * 24 + i]! * DB[k * 24 + j]!;
        Ke[i * 24 + j]! += sum * detW;
      }
    }
  }
  return Ke;
}

/**
 * Von Mises equivalent stress from a 6-vector [σxx,σyy,σzz,τxy,τyz,τzx]
 * (offset `o`). The 3× on the shear terms is load-bearing: pure shear τ must
 * give √3·τ, not τ.
 */
export const vonMises = (s: Float64Array | number[], o = 0): number =>
  Math.sqrt(
    0.5 * ((s[o]! - s[o + 1]!) ** 2 + (s[o + 1]! - s[o + 2]!) ** 2 + (s[o + 2]! - s[o]!) ** 2) +
    3 * (s[o + 3]! ** 2 + s[o + 4]! ** 2 + s[o + 5]! ** 2),
  );

/** Exact point-to-triangle distance (Ericson, Real-Time Collision Detection). */
function pointTriDist(
  px: number, py: number, pz: number,
  ax: number, ay: number, az: number,
  bx: number, by: number, bz: number,
  cx: number, cy: number, cz: number,
): number {
  const abx = bx - ax, aby = by - ay, abz = bz - az;
  const acx = cx - ax, acy = cy - ay, acz = cz - az;
  const apx = px - ax, apy = py - ay, apz = pz - az;
  const d1 = abx * apx + aby * apy + abz * apz;
  const d2 = acx * apx + acy * apy + acz * apz;
  const sq = (x: number, y: number, z: number): number => x * x + y * y + z * z;
  if (d1 <= 0 && d2 <= 0) return Math.sqrt(sq(apx, apy, apz));
  const bpx = px - bx, bpy = py - by, bpz = pz - bz;
  const d3 = abx * bpx + aby * bpy + abz * bpz;
  const d4 = acx * bpx + acy * bpy + acz * bpz;
  if (d3 >= 0 && d4 <= d3) return Math.sqrt(sq(bpx, bpy, bpz));
  const vc = d1 * d4 - d3 * d2;
  if (vc <= 0 && d1 >= 0 && d3 <= 0) {
    const v = d1 / (d1 - d3);
    return Math.sqrt(sq(apx - v * abx, apy - v * aby, apz - v * abz));
  }
  const cpx = px - cx, cpy = py - cy, cpz = pz - cz;
  const d5 = abx * cpx + aby * cpy + abz * cpz;
  const d6 = acx * cpx + acy * cpy + acz * cpz;
  if (d6 >= 0 && d5 <= d6) return Math.sqrt(sq(cpx, cpy, cpz));
  const vb = d5 * d2 - d1 * d6;
  if (vb <= 0 && d2 >= 0 && d6 <= 0) {
    const w = d2 / (d2 - d6);
    return Math.sqrt(sq(apx - w * acx, apy - w * acy, apz - w * acz));
  }
  const va = d3 * d6 - d5 * d4;
  if (va <= 0 && d4 - d3 >= 0 && d5 - d6 >= 0) {
    const w = (d4 - d3) / (d4 - d3 + (d5 - d6));
    return Math.sqrt(sq(
      px - (bx + w * (cx - bx)), py - (by + w * (cy - by)), pz - (bz + w * (cz - bz)),
    ));
  }
  const denom = 1 / (va + vb + vc);
  const v = vb * denom, w = vc * denom;
  return Math.sqrt(sq(
    px - (ax + abx * v + acx * w), py - (ay + aby * v + acy * w), pz - (az + abz * v + acz * w),
  ));
}

/**
 * The B-rep face nearest `point`, by EXACT point-to-triangle distance — a
 * pick lies at ~0 distance on its own face. (Centroid distance would let a
 * densely-tessellated neighbor steal the pick from a large planar face whose
 * few huge triangles have far-away centroids.)
 */
export function nearestFaceTriangle(
  P: Float32Array,
  I: Uint32Array,
  point: readonly [number, number, number],
): number {
  const nTri = I.length / 3;
  let best = -1, bestD = Infinity;
  for (let t = 0; t < nTri; t++) {
    const a = I[t * 3]! * 3, b = I[t * 3 + 1]! * 3, c = I[t * 3 + 2]! * 3;
    const d = pointTriDist(
      point[0], point[1], point[2],
      P[a]!, P[a + 1]!, P[a + 2]!, P[b]!, P[b + 1]!, P[b + 2]!, P[c]!, P[c + 1]!, P[c + 2]!,
    );
    if (d < bestD) { bestD = d; best = t; }
  }
  return best;
}

/** All triangle indices belonging to the B-rep face nearest `point`. */
function faceTriangles(input: FeaInput, point: [number, number, number]): number[] {
  const { positions: P, indices: I, triFaceIndex } = input;
  const best = nearestFaceTriangle(P, I, point);
  if (best < 0) return [];
  if (!triFaceIndex) return [best];
  const face = triFaceIndex[best]!;
  const out: number[] = [];
  const nTri = I.length / 3;
  for (let t = 0; t < nTri; t++) if (triFaceIndex[t] === face) out.push(t);
  return out;
}

export function solveFea(input: FeaInput): FeaResult {
  const P = input.positions, I = input.indices;
  const progress = input.onProgress ?? ((): void => undefined);
  if (input.fixtures.length === 0) throw new Error('FEA needs at least one fixture face');
  if (input.loads.length === 0) throw new Error('FEA needs at least one load');

  // --- bounding box -----------------------------------------------------------
  const min = [Infinity, Infinity, Infinity], max = [-Infinity, -Infinity, -Infinity];
  for (let i = 0; i < P.length; i += 3) {
    for (let k = 0; k < 3; k++) {
      const v = P[i + k]!;
      if (v < min[k]!) min[k] = v;
      if (v > max[k]!) max[k] = v;
    }
  }
  const ext = [max[0]! - min[0]!, max[1]! - min[1]!, max[2]! - min[2]!];
  const longest = Math.max(...ext);
  if (!(longest > 0)) throw new Error('Degenerate body');

  // --- voxelize (auto-coarsen to the element budget) ---------------------------
  const budget = input.maxElements ?? 40_000;
  // Thin bodies need ≥2 cells through their smallest extent or every ray
  // misses the slab — cap h at half the smallest nonzero bbox extent.
  const minExt = Math.min(...ext.filter((v) => v > 1e-9));
  const hCap = minExt / 2;
  let res = Math.min(96, Math.max(8, Math.round(input.resolution ?? 36)));
  let h = 0, nx = 0, ny = 0, nz = 0;
  let active!: Uint8Array;
  let nActive = 0;
  for (let attempt = 0; ; attempt++) {
    h = Math.min(longest / res, hCap);
    nx = Math.max(1, Math.ceil(ext[0]! / h - 1e-9));
    ny = Math.max(1, Math.ceil(ext[1]! / h - 1e-9));
    nz = Math.max(1, Math.ceil(ext[2]! / h - 1e-9));
    if (nx * ny * nz > 400_000) {
      throw new Error(
        `Body is too slender to voxelize within budget (${Math.round(longest / minExt)}:1 aspect) — analyze a simplified/shorter segment`,
      );
    }
    active = voxelize(P, I, min as [number, number, number], h, nx, ny, nz);
    nActive = 0;
    for (let i = 0; i < active.length; i++) nActive += active[i]!;
    // Coarsening only helps while h is resolution-bound, not thinness-bound.
    if (nActive <= budget || attempt >= 2 || res <= 8 || h >= hCap - 1e-12) break;
    res = Math.max(8, Math.floor(res * Math.cbrt(budget / nActive) * 0.97));
  }
  if (nActive === 0) {
    throw new Error(
      'Voxelization found no interior — the body may not be a closed solid, or a feature is thinner than half a voxel (raise resolution)',
    );
  }
  progress('voxelize', 1);

  // --- index active elements & nodes -------------------------------------------
  const NNX = nx + 1, NNY = ny + 1;
  const fullNode = (i: number, j: number, k: number): number => i + j * NNX + k * NNX * NNY;
  const nodeMap = new Int32Array(NNX * NNY * (nz + 1)).fill(-1);
  const elemNodes = new Int32Array(nActive * 8);
  const elemCell = new Int32Array(nActive); // linear cell id per active element
  let nNodes = 0, e = 0;
  for (let k = 0; k < nz; k++) for (let j = 0; j < ny; j++) for (let i = 0; i < nx; i++) {
    if (!active[i + j * nx + k * nx * ny]) continue;
    elemCell[e] = i + j * nx + k * nx * ny;
    for (let n = 0; n < 8; n++) {
      const [di, dj, dk] = NODE_OFF[n]!;
      const fn = fullNode(i + di, j + dj, k + dk);
      if (nodeMap[fn] === -1) nodeMap[fn] = nNodes++;
      elemNodes[e * 8 + n] = nodeMap[fn]!;
    }
    e++;
  }
  const ndof = nNodes * 3;

  // Node world positions (for BC selection, hotspot reporting, mapping).
  const nodePos = new Float32Array(nNodes * 3);
  for (let k = 0; k <= nz; k++) for (let j = 0; j <= ny; j++) for (let i = 0; i <= nx; i++) {
    const cn = nodeMap[fullNode(i, j, k)]!;
    if (cn === -1) continue;
    nodePos[cn * 3] = min[0]! + i * h;
    nodePos[cn * 3 + 1] = min[1]! + j * h;
    nodePos[cn * 3 + 2] = min[2]! + k * h;
  }

  // --- boundary conditions ------------------------------------------------------
  const nearFaceNodes = (point: [number, number, number]): number[] => {
    const tris = faceTriangles(input, point);
    const tol = h * 0.87;
    const picked = new Set<number>();
    for (const t of tris) {
      const a = I[t * 3]! * 3, b = I[t * 3 + 1]! * 3, c = I[t * 3 + 2]! * 3;
      const tmin = [0, 1, 2].map((k) => Math.min(P[a + k]!, P[b + k]!, P[c + k]!) - tol);
      const tmax = [0, 1, 2].map((k) => Math.max(P[a + k]!, P[b + k]!, P[c + k]!) + tol);
      const i0 = Math.max(0, Math.floor((tmin[0]! - min[0]!) / h));
      const i1 = Math.min(nx, Math.ceil((tmax[0]! - min[0]!) / h));
      const j0 = Math.max(0, Math.floor((tmin[1]! - min[1]!) / h));
      const j1 = Math.min(ny, Math.ceil((tmax[1]! - min[1]!) / h));
      const k0 = Math.max(0, Math.floor((tmin[2]! - min[2]!) / h));
      const k1 = Math.min(nz, Math.ceil((tmax[2]! - min[2]!) / h));
      for (let k = k0; k <= k1; k++) for (let j = j0; j <= j1; j++) for (let i = i0; i <= i1; i++) {
        const cn = nodeMap[fullNode(i, j, k)]!;
        if (cn === -1 || picked.has(cn)) continue;
        const px = min[0]! + i * h, py = min[1]! + j * h, pz = min[2]! + k * h;
        if (
          pointTriDist(px, py, pz, P[a]!, P[a + 1]!, P[a + 2]!, P[b]!, P[b + 1]!, P[b + 2]!, P[c]!, P[c + 1]!, P[c + 2]!) <= tol
        ) picked.add(cn);
      }
    }
    return [...picked];
  };

  const fixed = new Uint8Array(ndof);
  let nFixedNodes = 0;
  for (const fx of input.fixtures) {
    const nodes = nearFaceNodes(fx.point);
    nFixedNodes += nodes.length;
    for (const n of nodes) { fixed[n * 3] = fixed[n * 3 + 1] = fixed[n * 3 + 2] = 1; }
  }
  if (nFixedNodes === 0) throw new Error('Fixture selected no mesh nodes — pick a point ON a face');

  const f = new Float64Array(ndof);
  for (const ld of input.loads) {
    const nodes = nearFaceNodes(ld.point);
    if (nodes.length === 0) throw new Error('Load selected no mesh nodes — pick a point ON a face');
    const [fx0, fy0, fz0] = ld.force;
    for (const n of nodes) {
      f[n * 3]! += fx0 / nodes.length;
      f[n * 3 + 1]! += fy0 / nodes.length;
      f[n * 3 + 2]! += fz0 / nodes.length;
    }
  }
  for (let d = 0; d < ndof; d++) if (fixed[d]) f[d] = 0;

  // --- assemble diagonal + solve (matrix-free Jacobi-PCG) -----------------------
  const Ke = hexStiffness(input.E, input.nu, h);
  const diag = new Float64Array(ndof);
  for (let el = 0; el < nActive; el++) {
    for (let a = 0; a < 8; a++) {
      const n = elemNodes[el * 8 + a]! * 3;
      for (let d = 0; d < 3; d++) {
        const li = a * 3 + d;
        diag[n + d]! += Ke[li * 24 + li]!;
      }
    }
  }
  for (let d = 0; d < ndof; d++) if (fixed[d] || diag[d] === 0) diag[d] = 1;

  const u = new Float64Array(ndof);
  const r = Float64Array.from(f);
  const z = new Float64Array(ndof);
  const p = new Float64Array(ndof);
  const q = new Float64Array(ndof);
  const pe = new Float64Array(24), qe = new Float64Array(24);

  const matvec = (src: Float64Array, dst: Float64Array): void => {
    dst.fill(0);
    for (let el = 0; el < nActive; el++) {
      const eb = el * 8;
      for (let a = 0; a < 8; a++) {
        const n = elemNodes[eb + a]! * 3;
        pe[a * 3] = src[n]!; pe[a * 3 + 1] = src[n + 1]!; pe[a * 3 + 2] = src[n + 2]!;
      }
      for (let i = 0; i < 24; i++) {
        const ro = i * 24;
        let s = 0;
        for (let j = 0; j < 24; j++) s += Ke[ro + j]! * pe[j]!;
        qe[i] = s;
      }
      for (let a = 0; a < 8; a++) {
        const n = elemNodes[eb + a]! * 3;
        dst[n]! += qe[a * 3]!; dst[n + 1]! += qe[a * 3 + 1]!; dst[n + 2]! += qe[a * 3 + 2]!;
      }
    }
    for (let d = 0; d < ndof; d++) if (fixed[d]) dst[d] = 0;
  };

  let bnorm = 0;
  for (let d = 0; d < ndof; d++) bnorm += r[d]! * r[d]!;
  bnorm = Math.sqrt(bnorm);
  if (bnorm === 0) throw new Error('Zero load — nothing to solve');
  const tol = (input.tolerance ?? 1e-4) * bnorm;
  const maxIt = input.maxIterations ?? 1500;

  for (let d = 0; d < ndof; d++) z[d] = r[d]! / diag[d]!;
  p.set(z);
  let rz = 0;
  for (let d = 0; d < ndof; d++) rz += r[d]! * z[d]!;

  let it = 0;
  let converged = false;
  for (; it < maxIt; it++) {
    matvec(p, q);
    let pq = 0;
    for (let d = 0; d < ndof; d++) pq += p[d]! * q[d]!;
    if (!(pq > 0)) break; // singular / floating body
    const alpha = rz / pq;
    let rr = 0;
    for (let d = 0; d < ndof; d++) {
      u[d]! += alpha * p[d]!;
      r[d]! -= alpha * q[d]!;
      rr += r[d]! * r[d]!;
    }
    if (Math.sqrt(rr) < tol) { converged = true; it++; break; }
    let rzNew = 0;
    for (let d = 0; d < ndof; d++) {
      z[d] = r[d]! / diag[d]!;
      rzNew += r[d]! * z[d]!;
    }
    const beta = rzNew / rz;
    rz = rzNew;
    for (let d = 0; d < ndof; d++) p[d] = z[d]! + beta * p[d]!;
    if (it % 25 === 0) progress('solve', Math.min(0.99, it / maxIt));
  }
  progress('solve', 1);

  // --- stress recovery (centroid stress → nodal average → von Mises) ------------
  const B0 = bMatrix(0, 0, 0, h);
  const D = elasticityMatrix(input.E, input.nu);
  const sigmaSum = new Float64Array(nNodes * 6);
  const sigmaCount = new Float64Array(nNodes);
  const eps = new Float64Array(6), sig = new Float64Array(6);
  for (let el = 0; el < nActive; el++) {
    const eb = el * 8;
    for (let a = 0; a < 8; a++) {
      const n = elemNodes[eb + a]! * 3;
      pe[a * 3] = u[n]!; pe[a * 3 + 1] = u[n + 1]!; pe[a * 3 + 2] = u[n + 2]!;
    }
    for (let i = 0; i < 6; i++) {
      let s = 0;
      for (let j = 0; j < 24; j++) s += B0[i * 24 + j]! * pe[j]!;
      eps[i] = s;
    }
    for (let i = 0; i < 6; i++) {
      let s = 0;
      for (let j = 0; j < 6; j++) s += D[i * 6 + j]! * eps[j]!;
      sig[i] = s;
    }
    for (let a = 0; a < 8; a++) {
      const n = elemNodes[eb + a]!;
      for (let i = 0; i < 6; i++) sigmaSum[n * 6 + i]! += sig[i]!;
      sigmaCount[n]! += 1;
    }
  }
  const nodeVM = new Float64Array(nNodes);
  let maxVM = 0, hotNode = 0, maxDisp = 0, dispNode = 0;
  for (let n = 0; n < nNodes; n++) {
    const c = sigmaCount[n]! || 1;
    for (let i = 0; i < 6; i++) sig[i] = sigmaSum[n * 6 + i]! / c;
    const vm = vonMises(sig);
    nodeVM[n] = vm;
    if (vm > maxVM) { maxVM = vm; hotNode = n; }
    const d2 = u[n * 3]! ** 2 + u[n * 3 + 1]! ** 2 + u[n * 3 + 2]! ** 2;
    if (d2 > maxDisp) { maxDisp = d2; dispNode = n; }
  }
  maxDisp = Math.sqrt(maxDisp);
  progress('stress', 1);

  // --- map von Mises onto the display mesh vertices ------------------------------
  // Features thinner than a voxel have no elements: their vertices find no
  // nearby active node and are counted so callers can warn instead of the
  // ramp silently reading "0 MPa = safe" there.
  const nVerts = P.length / 3;
  const vertexVonMises = new Float32Array(nVerts);
  const vertexDisplacement = new Float32Array(nVerts * 3);
  let unmappedVertices = 0;
  for (let v = 0; v < nVerts; v++) {
    const gi = Math.round((P[v * 3]! - min[0]!) / h);
    const gj = Math.round((P[v * 3 + 1]! - min[1]!) / h);
    const gk = Math.round((P[v * 3 + 2]! - min[2]!) / h);
    let best = -1, bestD = Infinity;
    for (let rad = 0; rad <= 4 && best === -1; rad++) {
      for (let dk = -rad; dk <= rad; dk++) for (let dj = -rad; dj <= rad; dj++) for (let di = -rad; di <= rad; di++) {
        const i = gi + di, j = gj + dj, k = gk + dk;
        if (i < 0 || j < 0 || k < 0 || i > nx || j > ny || k > nz) continue;
        const cn = nodeMap[fullNode(i, j, k)]!;
        if (cn === -1) continue;
        const dx = nodePos[cn * 3]! - P[v * 3]!;
        const dy = nodePos[cn * 3 + 1]! - P[v * 3 + 1]!;
        const dz = nodePos[cn * 3 + 2]! - P[v * 3 + 2]!;
        const d = dx * dx + dy * dy + dz * dz;
        if (d < bestD) { bestD = d; best = cn; }
      }
      if (best !== -1) break;
    }
    if (best === -1) unmappedVertices++;
    vertexVonMises[v] = best !== -1 ? nodeVM[best]! : 0;
    if (best !== -1) {
      vertexDisplacement[v * 3] = u[best * 3]!;
      vertexDisplacement[v * 3 + 1] = u[best * 3 + 1]!;
      vertexDisplacement[v * 3 + 2] = u[best * 3 + 2]!;
    }
  }

  return {
    converged,
    iterations: it,
    elements: nActive,
    nodes: nNodes,
    voxelSize: h,
    maxVonMises: maxVM,
    hotspot: [nodePos[hotNode * 3]!, nodePos[hotNode * 3 + 1]!, nodePos[hotNode * 3 + 2]!],
    maxDisplacement: maxDisp,
    maxDisplacementAt: [
      nodePos[dispNode * 3]!, nodePos[dispNode * 3 + 1]!, nodePos[dispNode * 3 + 2]!,
    ],
    factorOfSafety: maxVM > 0 ? input.yieldStrength / maxVM : Infinity,
    vertexVonMises,
    vertexDisplacement,
    unmappedVertices,
  };
}

/**
 * Solid voxelization: parity of surface crossings along +X rays through each
 * (j,k) column of voxel centers. Watertight tessellations classify exactly;
 * ray origins are jittered off grid alignment to dodge edge/vertex tangency.
 */
export function voxelize(
  P: Float32Array,
  I: Uint32Array,
  origin: [number, number, number],
  h: number,
  nx: number,
  ny: number,
  nz: number,
): Uint8Array {
  const active = new Uint8Array(nx * ny * nz);
  const jitterY = h * 1.3e-4, jitterZ = h * 2.7e-4;
  // Bucket triangles by the (j,k) columns their AABBs cover.
  const cols: number[][] = Array.from({ length: ny * nz }, () => []);
  const nTri = I.length / 3;
  for (let t = 0; t < nTri; t++) {
    const a = I[t * 3]! * 3, b = I[t * 3 + 1]! * 3, c = I[t * 3 + 2]! * 3;
    const minY = Math.min(P[a + 1]!, P[b + 1]!, P[c + 1]!);
    const maxY = Math.max(P[a + 1]!, P[b + 1]!, P[c + 1]!);
    const minZ = Math.min(P[a + 2]!, P[b + 2]!, P[c + 2]!);
    const maxZ = Math.max(P[a + 2]!, P[b + 2]!, P[c + 2]!);
    const j0 = Math.max(0, Math.floor((minY - origin[1] - jitterY) / h - 0.5));
    const j1 = Math.min(ny - 1, Math.ceil((maxY - origin[1] - jitterY) / h - 0.5));
    const k0 = Math.max(0, Math.floor((minZ - origin[2] - jitterZ) / h - 0.5));
    const k1 = Math.min(nz - 1, Math.ceil((maxZ - origin[2] - jitterZ) / h - 0.5));
    for (let k = k0; k <= k1; k++) for (let j = j0; j <= j1; j++) cols[j + k * ny]!.push(t);
  }
  const xs: number[] = [];
  for (let k = 0; k < nz; k++) {
    for (let j = 0; j < ny; j++) {
      const tris = cols[j + k * ny]!;
      if (tris.length === 0) continue;
      const oy = origin[1] + (j + 0.5) * h + jitterY;
      const oz = origin[2] + (k + 0.5) * h + jitterZ;
      xs.length = 0;
      // Ray (ox→+∞, oy, oz): Möller–Trumbore specialized for dir (1,0,0).
      for (const t of tris) {
        const a = I[t * 3]! * 3, b = I[t * 3 + 1]! * 3, c = I[t * 3 + 2]! * 3;
        const ax = P[a]!, ay = P[a + 1]!, az = P[a + 2]!;
        const e1x = P[b]! - ax, e1y = P[b + 1]! - ay, e1z = P[b + 2]! - az;
        const e2x = P[c]! - ax, e2y = P[c + 1]! - ay, e2z = P[c + 2]! - az;
        // hvec = dir × e2 = (0, -e2z, e2y)
        const det = e1y * -e2z + e1z * e2y; // e1·hvec
        if (Math.abs(det) < 1e-14) continue;
        const inv = 1 / det;
        const sx = -ax, sy = oy - ay, sz = oz - az; // s = o - a (ox folded into t)
        const uu = (sy * -e2z + sz * e2y) * inv; // s·hvec
        if (uu < 0 || uu > 1) continue;
        // qvec = s × e1
        const qx = sy * e1z - sz * e1y, qy = sz * e1x - sx * e1z, qz = sx * e1y - sy * e1x;
        const vv = qx * inv; // dir·qvec = qx
        if (vv < 0 || uu + vv > 1) continue;
        const tt = (e2x * qx + e2y * qy + e2z * qz) * inv; // e2·qvec
        xs.push(tt); // world-x of the crossing (ray origin at x=0 plane)
      }
      if (xs.length < 2) continue;
      xs.sort((m, n) => m - n);
      for (let i = 0; i < nx; i++) {
        const xc = origin[0] + (i + 0.5) * h;
        let crossings = 0;
        for (const x of xs) { if (x < xc) crossings++; else break; }
        if (crossings & 1) active[i + j * nx + k * nx * ny] = 1;
      }
    }
  }
  return active;
}
